import '../styles/tooltip.css' // Import custom CSS

import React from 'react'
import { Checkbox } from '@radix-ui/themes'

import { AppPath } from '../enums/AppPath'
import { ConsignmentState, ReservationState, SalenoteState, VehicleState } from '../enums/DocStates'
import { makeUrlImageShort } from '../hooks/connectionData'
import {
  clientHrefReturn,
  formatCurrency,
  formatDateToDDMMYYYY,
  formatWithThousandSep,
  vehicleHrefReturn,
} from '../utils/utils'
import CardDto from '../interfaces/CardDto'
import { validateAndIdentifyRut } from '../utils/rutValidation'
import gearboxTranslate from '../utils/gearboxTranslate'
import fuelTranslate from '../utils/fuelTranslate'
import stateTranslate from '../utils/stateTranslate'
import { useAuth } from '../hooks/useAuth'
import { PageState } from '../interfaces/PageState'

import CustomLink from './CustomLink'
import HeicSafeImage from './HeicSafeImage'
import Salesman from './Salesman'

interface CardOneProps {
  card: CardDto
  docuType: string
  estado: number
  onClickCard?: (id: string) => void
  pageState?: PageState
  // New selection props
  isSelectionMode?: boolean
  isSelected?: boolean
  onSelectionChange?: (vehicleId: string) => void
}

interface CardSettings {
  titulo: string
  href: string
  fechaFin: string
  tooltip: string
}

/**
 * It shows the card with the car image and the car data, cient, user of the organization.
 */
const CardOne: React.FC<CardOneProps> = ({
  card,
  docuType,
  estado,
  onClickCard,
  pageState,
  isSelectionMode = false,
  isSelected = false,
  onSelectionChange,
}) => {
  const { userData, selectedPair, setSelectedPair } = useAuth()
  const client = card.client
  const vehicle = card.vehicle
  const imagen = vehicle?.firstImageUrl ?? ''
  const user = card.user
  const hayPrecios = !!card.price
  const precioVeh = formatCurrency(vehicle?.price ?? 0)
  const precioCli = hayPrecios ? formatCurrency(card.price) : ''
  const vendedor = `${user?.firstname ?? ''} ${user?.lastname ?? ''}`
  const createdAt = formatDateToDDMMYYYY(new Date(card.createdAt))

  let hayImagen = false
  let imagenUrl = ';'

  if (imagen && imagen.trim() !== '') {
    hayImagen = true
    imagenUrl = makeUrlImageShort(imagen)
  }
  const clienteNombre = `${client.firstname} ${client.lastname || ''}`.trim()
  
  const getClientType = () => {
    if (client.type) return client.type;
    if (client.nin) return validateAndIdentifyRut(client.nin).type;
    return 'Natural Person'; // Fallback
  };
  const isCompany = getClientType() === 'Legal Entity';

  const vehiculo = vehicle ? `${vehicle?.brand} ${vehicle?.model} ${vehicle?.version ?? ''} (${vehicle?.year})` : ''
  const cardSettings = tituloCard(docuType, card, estado)
  let reservado = ''
  let disabled = false
  let odometer
  let gearbox
  let fuel
  let state
  let branch_name
  let plate

  // For some rare reason, sometimes enters with vehicle = Undef (or null).
  // It must be because it is asynchronous.
  if (docuType === AppPath.Vehicles) {
    if (estado === VehicleState.NotasVendibles && vehicle?.reserved) {
      reservado = 'Reservado'
      disabled = true
    }

    odometer = formatWithThousandSep(vehicle?.odometer ?? 0)
    gearbox = gearboxTranslate[vehicle?.gearbox ?? ''] || (vehicle?.gearbox ?? '')
    fuel = fuelTranslate[vehicle?.fuel ?? ''] || (vehicle?.fuel ?? '')
    state = stateTranslate[vehicle?.state ?? ''] || (vehicle?.state ?? '')
    branch_name = vehicle?.branch
    plate = vehicle?.plate

    // WE WILL BE ABOUT THE ARRAY OF BRANCHES
    for (const branch of userData?.branches ?? []) {
      if (branch._id === vehicle?.branch) {
        branch_name = branch.name
        break
      }
    }
  }

  // This is for the case of the client and others
  const plate_format = vehicle?.plate ? `[${vehicle?.plate}]` : ''

  // Care here: Coordinate with Clienthrefregn and
  const putVehicleId = docuType === AppPath.Vehicles && estado >= VehicleState.Cotizables
  const putClientId = docuType === AppPath.Clients && estado >= VehicleState.Cotizables

  const handleBeforeNavigate = () => {
    if (onClickCard) {
      onClickCard(card.id)
    } else if (putVehicleId) {
      setSelectedPair({ ...selectedPair, vehicleId: card.id })
    } else if (putClientId) {
      setSelectedPair({ ...selectedPair, clientId: card.id })
    }
  }

  const handleSelectionClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onSelectionChange?.(card.id)
  }

  return (
    <div
      className={`bg-white shadow-lg rounded-lg relative ${isSelectionMode ? 'border-2' : ''} ${
        isSelected ? 'border-blue-500 bg-blue-50' : isSelectionMode ? 'border-gray-200' : ''
      }`}
    >
      {isSelectionMode && (
        <div className="absolute top-2 left-2 z-10">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelectionChange?.(card.id)}
            onClick={handleSelectionClick}
          />
        </div>
      )}
      <CustomLink
        href={cardSettings.href}
        className={`flex ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''} ${
          isSelectionMode ? 'pointer-events-none' : ''
        }`}
        disabled={disabled || isSelectionMode}
        onBeforeNavigate={handleBeforeNavigate}
        pageState={pageState}
      >
        {hayImagen && (
          <HeicSafeImage
            src={imagenUrl}
            alt="Vehículo"
            className="h-72 w-72 object-contain object-center rounded-l-lg"
          />
        )}
        <div className="p-4 flex-1">
          <div className="text-blue-500 font-bold text-xl mb-2">{cardSettings.titulo}</div>
          <div className="text-xl mb-1 flex items-center gap-2">
            {clienteNombre}
            {isCompany && (
              <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-0.5 rounded-full font-medium border border-indigo-200">
                Empresa
              </span>
            )}
          </div>
          <div className="flex flex-col space-y-1">
            <div className="text-gray-700">{client.email}</div>
            <div className="text-gray-700">{client.phone}</div>
          </div>
          <div className="flex flex-col space-y-1">
            <div className="text-gray-700">{vehiculo}</div>

            {docuType === AppPath.Vehicles ? (
              <div className="grid grid-cols-[auto,1fr] gap-1 text-gray-700">
                <div>Patente</div>
                <div>{plate}</div>
                <div>Precio</div>
                <div>{precioVeh}</div>
                <div>Km</div>
                <div>{odometer}</div>
                <div>Transmisión</div>
                <div>{gearbox}</div>
                <div>Combustible</div>
                <div>{fuel}</div>
                <div>Estado</div>
                <div>{state}</div>
                <div>Ingresado</div>
                <div>{createdAt}</div>
                <div>Sucursal</div>
                <div>{branch_name}</div>
              </div>
            ) : (
              <div>{plate_format}</div>
            )}
            {reservado !== '' && <div className="border-2 font-bold">{reservado}</div>}

            {/* Show sale note details for completed reservations */}
            {docuType === AppPath.Reservations && estado === ReservationState.Completado && card.salenote && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                <div className="text-sm font-semibold text-green-800 mb-1">Nota de Venta Asociada:</div>
                <div className="grid grid-cols-[auto,1fr] gap-1 text-sm text-green-700">
                  <div>Número:</div>
                  <div>
                    <a
                      href={`/${AppPath.SaleNotes}/view/${card.salenote._id}`}
                      className="text-blue-600 hover:text-blue-800 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      NV {card.salenote.number}
                    </a>
                  </div>
                  <div>Total:</div>
                  <div>{formatCurrency(card.salenote.total)}</div>
                  {card.salenote.approvedAt && (
                    <>
                      <div>Aprobada:</div>
                      <div>{formatDateToDDMMYYYY(new Date(card.salenote.approvedAt))}</div>
                    </>
                  )}
                  {card.salenote.voidedAt && (
                    <>
                      <div>Anulada:</div>
                      <div>{formatDateToDDMMYYYY(new Date(card.salenote.voidedAt))}</div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {hayPrecios && (
            <div className="grid grid-cols-[auto,1fr] gap-1 text-gray-700">
              <div>Precio cliente</div>
              <div>:{precioCli}</div>
              <div>Precio publicado</div>
              <div>:{precioVeh}</div>
            </div>
          )}
          {vendedor && vendedor.trim() !== '' && <Salesman vendedor={vendedor} />}
        </div>
      </CustomLink>
    </div>
  )
}
const tituloCard = (docuType: string, card: CardDto, estado: number): CardSettings => {
  const id = card.id
  const number = card.number
  let titulo = ''
  let href = ''
  let fechaFin = ''
  let tooltip = ''

  switch (docuType) {
    case AppPath.Estimates:
      titulo = `Cotización ${number}`
      href = `/${AppPath.Estimates}/view/${id}`

      if (estado == 1) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.expiresAt))
        tooltip = 'Fecha de expiración'
      } else if (estado == 2) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.voidedAt))
        tooltip = 'Fecha de anulación'
      }
      break

    case AppPath.Settlements:
      titulo = `Liquidación ${number}`
      href = `/${AppPath.Settlements}/view/${id}`

      if (estado == 1) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.voidedAt))
        tooltip = 'Fecha de anulación'
      }
      break

    case AppPath.Purchases:
      titulo = `Compra ${number}`
      href = `/${AppPath.Purchases}/view/${id}`

      if (estado == 1) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.voidedAt))
        tooltip = 'Fecha de anulación'
      }
      break

    case AppPath.Reservations:
      if (number && number > 0) titulo = `Reserva ${number}`
      href = `/${AppPath.Reservations}/view/${id}`

      if (estado === ReservationState.Completado) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.completedAt))
        tooltip = 'Fecha completada'
      } else if (estado === ReservationState.Archivado) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.archivedAt))
        tooltip = 'Fecha archivada'
      } else if (estado === ReservationState.Nulo) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.voidedAt))
        tooltip = 'Fecha de anulación'
      }

      break

    case AppPath.SaleNotes:
      if (number && number > 0) titulo = `Nota de venta ${number}`
      href = `/${AppPath.SaleNotes}/view/${id}`

      if (estado === SalenoteState.Pendiente) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.completedAt))
        tooltip = 'Fecha creada'
      } else if (estado === SalenoteState.Completado) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.archivedAt))
        tooltip = 'Fecha completada'
      } else if (estado === SalenoteState.Nulo) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.voidedAt))
        tooltip = 'Fecha de anulación'
      }

      break

    case AppPath.Consignments:
      if (number && number > 0) titulo = `Consignación ${number}`
      href = `/${AppPath.Consignments}/view/${id}`

      if (estado === ConsignmentState.Nulo) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.voidedAt))
        tooltip = 'Fecha anulada'
      } else if (estado === ConsignmentState.Archivado) {
        fechaFin = formatDateToDDMMYYYY(new Date(card.archivedAt))
        tooltip = 'Fecha archivada'
      } else if (estado === ConsignmentState.Liquidables) {
        href = `/${AppPath.Settlements}/${AppPath.Consignments}/${id}`
      }
      break

    case AppPath.Vehicles:
      href = vehicleHrefReturn(estado, id)

      break

    case AppPath.Clients:
      href = clientHrefReturn(estado, id)

      break
  }

  return { titulo: `${titulo}`, href: href, fechaFin: fechaFin, tooltip: tooltip }
}
export default CardOne
